import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { MagicWand, Warning } from '@phosphor-icons/react';

const SuggestionRow = ({ s, checked, onToggle }) => (
  <label
    className={`flex items-start gap-3 px-3 py-2 border-b border-border last:border-b-0 cursor-pointer ${checked ? 'bg-brand/5' : 'opacity-60'}`}
    data-testid={`autolink-row-${s.tile_id}`}
  >
    <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`autolink-check-${s.tile_id}`} className="mt-0.5" />
    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between gap-2">
        <span className="font-bold text-sm">{s.tile_label}</span>
        <span className="text-[11px] font-mono text-muted-foreground">
          Zone {s.zone}{s.aisle ? ` · Aisle ${s.aisle}` : ''} · {s.locations.length} location{s.locations.length === 1 ? '' : 's'}
        </span>
      </div>
      <p className="text-[11px] font-mono text-muted-foreground truncate" title={s.locations.map((l) => l.location_code).join(', ')}>
        {s.locations.slice(0, 8).map((l) => l.location_code).join(', ')}{s.locations.length > 8 ? ` +${s.locations.length - 8} more` : ''}
      </p>
    </div>
  </label>
);

export const AutoLinkDialog = ({ data, onClose, onApply }) => {
  const [accepted, setAccepted] = useState(() => new Set(data.suggestions.map((s) => s.tile_id)));
  const toggle = (id) => setAccepted((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const chosen = data.suggestions.filter((s) => accepted.has(s.tile_id));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="autolink-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MagicWand size={16} weight="fill" className="text-brand" /> Suggested rack links
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Matched by each tile's zone and aisle. Untick any rack you want to skip — nothing changes until you apply.
          </p>
        </DialogHeader>
        {data.tiles_without_zone > 0 && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="autolink-missing-zone">
            <Warning size={12} className="inline mr-1" />
            {data.tiles_without_zone} rack/floor tile{data.tiles_without_zone === 1 ? ' has' : 's have'} no zone yet — set zone and aisle in the inspector to include them.
          </p>
        )}
        <div className="border border-border max-h-96 overflow-y-auto" data-testid="autolink-list">
          {data.suggestions.length === 0 ? (
            <p className="p-4 text-xs text-muted-foreground" data-testid="autolink-empty">
              No unlinked locations match the zones and aisles on your racks.
            </p>
          ) : data.suggestions.map((s) => (
            <SuggestionRow key={s.tile_id} s={s} checked={accepted.has(s.tile_id)} onToggle={() => toggle(s.tile_id)} />
          ))}
        </div>
        <DialogFooter className="justify-between flex-row gap-2">
          <span className="text-xs text-muted-foreground self-center" data-testid="autolink-selected-count">
            {chosen.length} of {data.suggestions.length} racks selected
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="autolink-cancel-btn">Cancel</Button>
            <Button
              onClick={() => onApply(chosen)}
              disabled={chosen.length === 0}
              className="bg-brand hover:bg-brand/90"
              data-testid="autolink-apply-btn"
            >
              Accept {chosen.length} rack{chosen.length === 1 ? '' : 's'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
